import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Avatar, Box, Card, CardHeader, FormControl, InputLabel, MenuItem, Select, TextField, Tooltip } from '@mui/material';
import { useState } from 'react';
import { useRootStore } from '../hooks/useRootStore';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { Employee } from '../data/Employee';
import { Gender } from '../data/Gender';
import CameraAltIcon from '@mui/icons-material/CameraAlt';

export default function ChangeEmployeeDialog({ employee, onSave, onCancel }: { employee: Employee, onSave: (changed: Employee) => void, onCancel: () => void }) {
    const [changed, setChanged] = useState(employee);

    const [firstNameNotSpecified, setFirstNameNotSpecified] = useState(false);
    const [lastNameNotSpecified, setLastNameNotSpecified] = useState(false);
    const [contactsNotSpecified, setContactsNotSpecified] = useState(false);
    const { api } = useRootStore();

    const save = async () => {
        let result = true;
        if ((changed.FirstName?.trim() ?? '') == '') {
            setFirstNameNotSpecified(true);
            result = false;
        };
        if ((changed.LastName?.trim() ?? '') == '') {
            setLastNameNotSpecified(true);
            result = false;
        };
        if (((changed.Phone?.trim() ?? '') == '')
            && ((changed.Email?.trim() ?? '') == '')
            && ((changed.Telegram?.trim()) == '')
            && ((changed.Skype?.trim() ?? '') == '')) {
            setContactsNotSpecified(true);
            result = false;
        };

        if (result) {
            await api.updateEmployee(changed)
                .then(() => onSave(changed));
        }
    }

    /*function toDataURL(url: string, callback: (base64: string | ArrayBuffer) => void) {
        const xhr = new XMLHttpRequest();
        xhr.onload = function () {
            const reader = new FileReader();
            reader.onloadend = function () {
                if (reader.result) {
                    callback(reader.result);
                }
            }
            reader.readAsDataURL(xhr.response);
        };
        xhr.open('GET', url);
        xhr.responseType = 'blob';
        xhr.send();
    }*/

    function uploadAvatar(file: File) {
        const reader = new FileReader();
        reader.onload = () => setChanged({ ...changed, Avatar: reader.result as string });
        reader.readAsDataURL(file);
    }

    return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <Dialog
                maxWidth='xl'
                fullWidth
                open={true}                
                onClose={onCancel}
            >
                <DialogTitle>Изменение профиля</DialogTitle>
                <DialogContent className='change-employee-dialog' sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <Card className="inputs" sx={{ display: 'flex', flexDirection: "column", border: contactsNotSpecified ? '1px solid red !important' : '' }}>                        
                        <label htmlFor="upload-avatar">
                            <Tooltip title="Изменить аватар">
                                <CameraAltIcon sx={{ cursor: 'pointer', position: 'absolute' }} />
                            </Tooltip>
                            <Avatar
                                component="label"
                                src={changed.Avatar}
                            >
                            </Avatar>
                            <input
                                type="file"
                                id="upload-avatar"
                                hidden
                                onChange={(e) => {
                                    if (e.target?.files?.length ?? 0 > 0) {
                                        uploadAvatar(e.target.files![0]);
                                    }
                                }}
                            />
                        </label>
                        <CardHeader title="Контакты" className="card-header" />
                        <Box><TextField label="Телефон" value={changed.Phone ?? ''} onChange={(e) => { setContactsNotSpecified(false); setChanged({ ...changed, Phone: e.target.value }); }} /></Box>
                        <Box><TextField label="Email" value={changed.Email ?? ''} onChange={(e) => { setContactsNotSpecified(false); setChanged({ ...changed, Email: e.target.value }); }} /></Box>
                        <Box><TextField label="Skype" value={changed.Skype ?? ''} onChange={(e) => { setContactsNotSpecified(false); setChanged({ ...changed, Skype: e.target.value }); }} /></Box>
                        <Box><TextField label="Телеграм" value={changed.Telegram ?? ''} onChange={(e) => { setContactsNotSpecified(false); setChanged({ ...changed, Telegram: e.target.value }); }} /></Box>
                    </Card>
                    <Card className="inputs" sx={{ display: 'flex', flexDirection: "column" }}>
                        <CardHeader title="Основные данные" className="card-header" />
                        <Box><TextField error={lastNameNotSpecified} label="Фамилия" value={changed.LastName ?? ''} onChange={(e) => { setLastNameNotSpecified(false); setChanged({ ...changed, LastName: e.target.value }); } } /></Box>
                        <Box><TextField error={firstNameNotSpecified} label="Имя" value={changed.FirstName ?? ''} onChange={(e) => { setFirstNameNotSpecified(false); setChanged({ ...changed, FirstName: e.target.value }); }} /></Box>
                        <Box><TextField label="Отчество" value={changed.MiddleName ?? ''} onChange={(e) => setChanged({ ...changed, MiddleName: e.target.value })} /></Box>
                        <Box>
                            <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="de" >
                                <DatePicker format="DD.MM.YYYY"
                                    label="Дата рождения" 
                                    value={changed.BirthDate ? moment(changed.BirthDate) : null}
                                    onChange={(e) => { if (e) setChanged({ ...changed, BirthDate: e.toDate() }) }}
                                />
                            </LocalizationProvider>
                        </Box>
                        <Box>
                            <FormControl fullWidth>
                                <InputLabel id="gender-select-label">Пол</InputLabel>
                                <Select
                                    labelId="gender-select-label"
                                    id="gender-select"
                                    value={changed.Gender.toString()}
                                    label="Пол"
                                    onChange={e => setChanged({ ...changed, Gender: e.target.value as unknown as number })}
                                >
                                    <MenuItem value={Gender.male}>Мужской</MenuItem>
                                    <MenuItem value={Gender.female}>Женский</MenuItem>
                                    <MenuItem value={Gender.undisclosed}>Не скажу</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                        <Box><TextField label="ИНН" value={changed.Inn ?? ''} onChange={(e) => setChanged({ ...changed, Inn: e.target.value })} /></Box>
                        <Box><TextField label="Наценка за предпочтение:" value={changed.IssuePreferredRate ?? ''} onChange={(e) => setChanged({ ...changed, IssuePreferredRate: e.target.value as unknown as number })} /></Box>
                    </Card>
                    <Card className="inputs" sx={{ display: 'flex', flex:1, flexDirection: "column" }}>
                        <CardHeader title="Реквизиты для переводов" className="card-header" />
                        <Box><TextField label="ИНН для оплаты" value={changed.PaymentVATno ?? ''} onChange={(e) => setChanged({ ...changed, PaymentVATno: e.target.value })} /></Box>                        
                        <Box><TextField label="Номер счета" value={changed.PaymentBankAccount ?? ''} onChange={(e) => setChanged({ ...changed, PaymentBankAccount: e.target.value })} /></Box>
                        <Box><TextField label="Наименование банка" value={changed.PaymentBankName ?? ''} onChange={(e) => setChanged({ ...changed, PaymentBankName: e.target.value })} /></Box>
                        <Box><TextField label="БИК" value={changed.PaymentBIC ?? ''} onChange={(e) => setChanged({ ...changed, PaymentBIC: e.target.value })} /></Box>
                        <Box><TextField label="Кор.счёт" value={changed.PaymentCorrAccNo ?? ''} onChange={(e) => setChanged({ ...changed, PaymentCorrAccNo: e.target.value })} /></Box>
                        <Box><TextField label="Местоположение банка" value={changed.PaymentBankLocation ?? ''} onChange={(e) => setChanged({ ...changed, PaymentBankLocation: e.target.value })} /></Box>
                        <Box><TextField label="Получатель платежа" value={changed.PaymentName ?? ''} onChange={(e) => setChanged({ ...changed, PaymentName: e.target.value })} /></Box>
                    </Card>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onCancel}>Отмена</Button>
                    <Button onClick={save}>Сохранить</Button>
                </DialogActions>
            </Dialog>
        </LocalizationProvider>
    );
}